import React, { useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import Card from '../common/Card';
import { useApi } from '../../hooks/useApi';
import { getItemHistory } from '../../api/client';
import { TrendingUp } from 'lucide-react';

const TIME_RANGES = [
  { label: '24H', value: '24h' },
  { label: '7D', value: '7d' },
  { label: '30D', value: '30d' },
  { label: 'ALL', value: 'all' },
];

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card p-3 text-xs">
      <p className="text-text-secondary mb-1 font-mono">{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }} className="font-mono">
          {entry.name}: ₽{entry.value?.toLocaleString()}
        </p>
      ))}
      {payload.length >= 2 && (
        <p className="text-accent-amber font-mono mt-1">
          Spread: ₽{Math.abs((payload[0].value || 0) - (payload[1].value || 0)).toLocaleString()}
        </p>
      )}
    </div>
  );
}

export default function PriceHistory({ classname, className = '' }) {
  const [range, setRange] = useState('7d');

  const { data: history, loading } = useApi(
    classname ? getItemHistory : () => Promise.resolve(null),
    classname ? [classname, { range }] : null,
    !!classname
  );

  const chartData = React.useMemo(() => {
    if (!history) return [];
    const arr = Array.isArray(history) ? history : history.data || [];
    return arr.map((d) => ({
      time: d.date || d.timestamp || d.period || '',
      buyPrice: d.buyPrice ?? d.buy_price ?? d.price ?? 0,
      sellPrice: d.sellPrice ?? d.sell_price ?? d.price ?? 0,
    }));
  }, [history]);

  return (
    <Card title="Price History" icon={TrendingUp} className={className}>
      {/* Time Range Selector */}
      <div className="flex items-center justify-end mb-4">
        <div className="flex gap-1">
          {TIME_RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`px-2 py-1 rounded text-[10px] font-mono transition-colors ${
                range === r.value
                  ? 'bg-accent-blue/10 text-accent-blue border border-accent-blue/20'
                  : 'text-text-muted hover:text-text-secondary'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {!classname ? (
        <div className="flex items-center justify-center h-[250px] text-text-muted text-sm">
          Select an item to view price history
        </div>
      ) : loading ? (
        <div className="skeleton h-[250px] rounded" />
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center h-[250px] text-text-muted text-sm">
          No price data available
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <AreaChart data={chartData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="buyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00ff88" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#00ff88" stopOpacity={0.01} />
              </linearGradient>
              <linearGradient id="sellGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ff3366" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#ff3366" stopOpacity={0.01} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="time"
              tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: '#1e293b' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: '#475569', fontSize: 10, fontFamily: 'JetBrains Mono' }}
              axisLine={{ stroke: '#1e293b' }}
              tickLine={false}
              tickFormatter={(v) => `₽${v}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
            <Area
              type="monotone"
              dataKey="buyPrice"
              name="Buy Price"
              stroke="#00ff88"
              fill="url(#buyGradient)"
              strokeWidth={2}
            />
            <Area
              type="monotone"
              dataKey="sellPrice"
              name="Sell Price"
              stroke="#ff3366"
              fill="url(#sellGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}
